import { type RefObject, useEffect } from 'react'

import { logger } from '../misc/logtail'

interface Position {
  x: number
  y: number
}

const on = <T extends Window | Document | HTMLElement | EventTarget>(
  object: T,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/no-unsafe-function-type
  ...arguments_: Parameters<T['addEventListener']> | [string, Function | null, ...any]
): void => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  object.addEventListener(...(arguments_ as Parameters<HTMLElement['addEventListener']>))
}

const off = <T extends Window | Document | HTMLElement | EventTarget>(
  object: T,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/no-unsafe-function-type
  ...arguments_: Parameters<T['removeEventListener']> | [string, Function | null, ...any]
): void => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  object.removeEventListener(...(arguments_ as Parameters<HTMLElement['removeEventListener']>))
}

export const useScroll = (reference: RefObject<HTMLElement>, handler: (position: Position) => void): void => {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition,sonarjs/different-types-comparison
  if (process.env.NODE_ENV === 'development' && (typeof reference !== 'object' || reference.current === undefined)) {
    logger.debug('`useScroll` expects a single ref argument.')
  }

  useEffect(() => {
    const target = reference.current
    if (target === null) return

    const listener = (): void => {
      handler({
        x: target.scrollLeft,
        y: target.scrollTop,
      })
    }

    on(target, 'scroll', listener, {
      capture: false,
      passive: true,
    })

    return (): void => {
      off(target, 'scroll', listener)
    }
  }, [handler, reference])
}
